import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../services/api";
import "./Login.css";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!token) {
      setError("Reset token is missing. Please request a new reset link.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await resetPassword({
        token,
        newPassword,
        confirmPassword,
      });
      setSuccessMessage(
        response?.message ||
          "Password reset successful. You can now sign in with your new password.",
      );
      setTimeout(() => navigate("/"), 1000);
    } catch (apiError) {
      setError(
        apiError.response?.data?.error ||
          "Unable to reset password. Please request a new link and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="hero">
          <div className="hero-icon">
            <div className="hero-icon-inner">
              <img
                src="/assets/logo.png"
                alt="Promoter Tracker Logo"
                style={{ width: "96px", height: "96px" }}
              />
            </div>
          </div>
          <h1>Set New Password</h1>
          <p className="subtitle">Use a strong password for your promoter account.</p>
        </div>

        <div className="content">
          <div className="login-header">
            <Link to="/" className="back" aria-label="Back to Login">
              ←
            </Link>
            <div className="header-title">Reset Password</div>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            <label className="login-field-label">
              New Password <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <div className="password-input-wrap">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNewPassword((value) => !value)}
                disabled={isSubmitting}
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
              >
                {showNewPassword ? (
                  <svg
                    className="password-toggle-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 3l18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.58 10.58a2 2 0 1 0 2.83 2.83"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.88 5.09A10.94 10.94 0 0 1 12 4.9c7 0 10.5 7.1 10.5 7.1a16.4 16.4 0 0 1-4.16 4.86M6.61 6.61A16.57 16.57 0 0 0 1.5 12s3.5 7.1 10.5 7.1c1.53 0 2.94-.33 4.2-.9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className="password-toggle-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M1.5 12s3.5-7.1 10.5-7.1S22.5 12 22.5 12 19 19.1 12 19.1 1.5 12 1.5 12z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                )}
              </button>
            </div>
            <label className="login-field-label">
              Confirm New Password <span className="required-asterisk" aria-hidden="true">*</span>
            </label>
            <div className="password-input-wrap">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword((value) => !value)}
                disabled={isSubmitting}
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirmPassword ? (
                  <svg
                    className="password-toggle-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 3l18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.58 10.58a2 2 0 1 0 2.83 2.83"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.88 5.09A10.94 10.94 0 0 1 12 4.9c7 0 10.5 7.1 10.5 7.1a16.4 16.4 0 0 1-4.16 4.86M6.61 6.61A16.57 16.57 0 0 0 1.5 12s3.5 7.1 10.5 7.1c1.53 0 2.94-.33 4.2-.9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className="password-toggle-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M1.5 12s3.5-7.1 10.5-7.1S22.5 12 22.5 12 19 19.1 12 19.1 1.5 12 1.5 12z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                )}
              </button>
            </div>

            {error && (
              <div className="form-feedback form-feedback-error" role="alert">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="form-feedback form-feedback-success" role="status">
                {successMessage}
              </div>
            )}

            <button className="submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <div className="footer-note">Version 1.0</div>
        </div>
      </div>
    </div>
  );
}
